import React, { useRef, useState } from 'react';
import { ImageIcon, Loader2, Upload, X } from 'lucide-react';
import ugcAdService from '../../services/api/ugcAdService';

/**
 * UGC動画広告の画像選択で使う1件分のデータ。`key` が `product_image_keys` に入る値
 * （商品選択なら `ProductImage.file_path`、アップロードなら upload-image が返す `s3_key`）。
 * song-ad の SongAdImageKeysField と同一仕様（アップロード先APIだけ /ugc-ads 系）。
 */
export interface UgcAdImageKeyItem {
  key: string;
  url: string | null;
  label: string;
  source: 'product' | 'upload';
}

interface UgcAdImageKeysFieldProps {
  items: UgcAdImageKeyItem[];
  onChange: (items: UgcAdImageKeyItem[]) => void;
  disabled?: boolean;
  /** 合計上限（目安）。既定3枚 */
  maxTotal?: number;
  /** アップロードボタンを隠す（一覧の作成パネル用。ローカルアップロードは①情報登録タブでのみ） */
  hideUpload?: boolean;
}

/**
 * 商品選択で選んだ画像＋ローカルアップロード画像を1つのサムネイル一覧として表示・管理する
 * 共有コンポーネント。`UgcAdCreatePanel` と `InfoStep` の両方から使う。
 */
const UgcAdImageKeysField: React.FC<UgcAdImageKeysFieldProps> = ({
  items,
  onChange,
  disabled = false,
  maxTotal = 3,
  hideUpload = false,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleRemove = (key: string) => {
    onChange(items.filter((i) => i.key !== key));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    setUploadError(null);
    const remaining = Math.max(0, maxTotal - items.length);
    const toProcess = files.slice(0, remaining);
    if (files.length > toProcess.length) {
      setUploadError(`画像は合計${maxTotal}枚までです。一部の画像はスキップされました。`);
    }

    const newItems: UgcAdImageKeyItem[] = [];
    setIsUploading(true);
    try {
      // eslint-disable-next-line no-restricted-syntax
      for (const file of toProcess) {
        if (!file.type.startsWith('image/')) {
          setUploadError(`${file.name} は画像ファイルではないためスキップしました`);
          continue;
        }
        if (file.size > 10 * 1024 * 1024) {
          setUploadError(`${file.name} は10MBを超えているためスキップしました`);
          continue;
        }
        try {
          // eslint-disable-next-line no-await-in-loop
          const { s3Key } = await ugcAdService.uploadImage(file);
          newItems.push({
            key: s3Key,
            url: URL.createObjectURL(file),
            label: file.name,
            source: 'upload',
          });
        } catch (err) {
          setUploadError(`${file.name} のアップロードに失敗しました（${ugcAdService.extractErrorMessage(err)}）`);
        }
      }
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }

    if (newItems.length > 0) {
      onChange([...items, ...newItems]);
    }
  };

  const atMax = items.length >= maxTotal;

  return (
    <div>
      {items.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {items.map((item) => (
            <div
              key={item.key}
              className="relative group w-16 h-16 rounded-lg overflow-hidden border border-warm-border bg-warm-surface"
              title={item.label}
            >
              {item.url ? (
                <img src={item.url} alt={item.label} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <ImageIcon className="w-5 h-5" />
                </div>
              )}
              <span className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-[9px] text-center leading-tight py-0.5">
                {item.source === 'product' ? '商品画像' : 'アップロード'}
              </span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => handleRemove(item.key)}
                  className="absolute top-0 right-0 p-0.5 bg-black/50 text-white rounded-bl opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {!hideUpload && (
        <>
          <button
            type="button"
            disabled={disabled || isUploading || atMax}
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-warm-border rounded-tasky-sm text-gray-600 hover:border-primary hover:text-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            画像をアップロードする
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />
          {atMax && !uploadError && (
            <p className="text-[11px] text-gray-400 mt-1">画像は合計{maxTotal}枚まで選択できます。</p>
          )}
          {uploadError && <p className="text-[11px] text-destructive mt-1">{uploadError}</p>}
        </>
      )}
    </div>
  );
};

export default UgcAdImageKeysField;
